import { Fragment } from "react";
import { cn } from "@/lib/utils";

const BAR_SEGMENTS = [
  { key: "asr_ms", label: "ASR", color: "bg-sky-500" },
  { key: "pilot_ms", label: "决策模型", color: "bg-violet-500" },
  { key: "llm_total", label: "LLM", color: "bg-emerald-500" },
  { key: "tts_wall_ms", label: "TTS", color: "bg-amber-500" },
] as const;

const TTFB_STAGES = [
  { key: "ttfb_asr_ms", label: "ASR", dot: "bg-sky-500" },
  { key: "ttfb_pilot_ms", label: "决策模型", dot: "bg-violet-500" },
  { key: "ttfb_talker_ms", label: "回复模型", dot: "bg-emerald-500" },
  { key: "ttfb_tts_ms", label: "TTS", dot: "bg-amber-500" },
] as const;

function n(v: unknown): number | null {
  return typeof v === "number" ? v : null;
}

export function TurnDebug({ debug }: { debug: Record<string, unknown> }) {
  // --- Bar ---
  const llmDur = n(debug.llm_duration_ms) ?? 0;
  const llmTotal = n(debug.ttfb_pilot_ms) !== null ? llmDur : 0;

  const barVals: Record<string, number> = {};
  for (const seg of BAR_SEGMENTS) {
    if (seg.key === "llm_total") {
      if (n(debug.llm_duration_ms) !== null && llmTotal > 0) barVals[seg.key] = llmTotal;
    } else {
      const v = n(debug[seg.key]);
      if (v !== null && v > 0) barVals[seg.key] = v;
    }
  }
  const activeBar = BAR_SEGMENTS.filter((s) => s.key in barVals);
  const totalBar = activeBar.reduce((sum, s) => sum + barVals[s.key], 0);

  // --- TTFB chain ---
  const overheadPre = n(debug.overhead_pre_ms);
  const deliveryMs = n(debug.delivery_ms);
  const ttfb: { label: string; dot: string; cum: number; delta: number | null }[] = [];
  // offset: shift cumulative values so chain starts from speech_end, not t0
  const offset = overheadPre ?? 0;
  if (overheadPre !== null) {
    ttfb.push({ label: "前处理", dot: "bg-zinc-400", cum: overheadPre, delta: null });
  }
  for (const stage of TTFB_STAGES) {
    const v = n(debug[stage.key]);
    if (v === null) continue;
    const cum = Math.round(v + offset);
    const prev = ttfb.length > 0 ? ttfb[ttfb.length - 1].cum : 0;
    ttfb.push({ label: stage.label, dot: stage.dot, cum, delta: prev > 0 ? Math.round(cum - prev) : null });
  }
  if (deliveryMs !== null && deliveryMs > 0) {
    const prev = ttfb.length > 0 ? ttfb[ttfb.length - 1].cum : 0;
    const cum = prev + deliveryMs;
    ttfb.push({ label: "传输", dot: "bg-rose-500", cum, delta: deliveryMs });
  }

  // --- Stats: group by stage ---
  const asrMs = n(debug.asr_ms);
  const asrAudioMs = n(debug.asr_audio_ms);
  const ttsWallMs = n(debug.tts_wall_ms);
  const ttsAudioMs = n(debug.tts_audio_ms);
  const gaps = n(debug.audio_gaps);
  const gapMs = n(debug.audio_gap_ms);

  const gpuAsr = n(debug.gpu_asr_ms) ?? 0;
  const gpuLlm = n(debug.gpu_llm_ms) ?? 0;
  const gpuTts = n(debug.gpu_tts_ms) ?? 0;
  const gpuTotal = gpuAsr + gpuLlm + gpuTts;
  const audioTotal = (asrAudioMs ?? 0) + (ttsAudioMs ?? 0);
  const gpuUtil = gpuTotal > 0 && audioTotal > 0 ? Math.round((gpuTotal / audioTotal) * 100) : null;

  const totalMs = n(debug.duration_ms);
  const cacheHit = typeof debug.cache_hit === "string" ? debug.cache_hit : null;

  // Build stage groups: each group is a nowrap span with stage label + all related metrics
  const groups: React.ReactNode[] = [];

  // ASR stage: gpu compute + audio duration + RTF
  if (gpuAsr > 0 || asrAudioMs !== null) {
    groups.push(
      <span className="whitespace-nowrap" key="asr">
        <span className="text-muted-foreground/60">ASR </span>
        <span className="tabular-nums">
          {gpuAsr > 0 && <>{gpuAsr}<span className="text-muted-foreground/40">ms </span></>}
          {asrAudioMs !== null && <>{(asrAudioMs / 1000).toFixed(1)}<span className="text-muted-foreground/40">s </span></>}
          {asrMs !== null && asrAudioMs ? <>×{(asrMs / asrAudioMs).toFixed(2)}</> : null}
        </span>
      </span>,
    );
  }

  // LLM stage: gpu compute only
  if (gpuLlm > 0) {
    groups.push(
      <span className="whitespace-nowrap" key="llm">
        <span className="text-muted-foreground/60">LLM </span>
        <span className="tabular-nums">{gpuLlm}<span className="text-muted-foreground/40">ms</span></span>
      </span>,
    );
  }

  // TTS stage: gpu compute + audio duration + RTF
  if (gpuTts > 0 || ttsAudioMs !== null) {
    groups.push(
      <span className="whitespace-nowrap" key="tts">
        <span className="text-muted-foreground/60">TTS </span>
        <span className="tabular-nums">
          {gpuTts > 0 && <>{gpuTts}<span className="text-muted-foreground/40">ms </span></>}
          {ttsAudioMs !== null && <>{(ttsAudioMs / 1000).toFixed(1)}<span className="text-muted-foreground/40">s </span></>}
          {ttsWallMs !== null && ttsAudioMs ? <>×{(ttsWallMs / ttsAudioMs).toFixed(2)}</> : null}
        </span>
      </span>,
    );
  }

  // GPU summary (Σ + utilization)
  if (gpuTotal > 0) {
    groups.push(
      <span className="whitespace-nowrap tabular-nums text-muted-foreground/40" key="sum">
        Σ{gpuTotal}<span>ms</span>{gpuUtil !== null && <> {gpuUtil}%</>}
      </span>,
    );
  }

  // Gaps (client-side audio playback quality)
  if (gaps !== null && gaps > 0) {
    groups.push(
      <span className="whitespace-nowrap" key="gaps">
        <span className="text-muted-foreground/60">播放间隙 </span>
        <span className="tabular-nums">{gaps}{gapMs !== null && gapMs > 0 && <> {Math.round(gapMs)}<span className="text-muted-foreground/40">ms</span></>}</span>
      </span>,
    );
  }

  // Cache hit
  if (cacheHit) {
    groups.push(
      <span className="whitespace-nowrap rounded bg-emerald-100 px-1 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300" key="cache">
        缓存： {cacheHit}
      </span>,
    );
  }

  // Total
  if (totalMs !== null) {
    groups.push(
      <span className="whitespace-nowrap" key="total">
        <span className="text-muted-foreground/60">总计 </span>
        <span className="tabular-nums">{Math.round(totalMs)}<span className="text-muted-foreground/40">ms</span></span>
      </span>,
    );
  }

  const hasBar = activeBar.length > 0 && totalBar > 0;
  const hasTtfb = ttfb.length > 0;
  const hasStats = groups.length > 0;

  if (!hasBar && !hasTtfb && !hasStats) return null;

  return (
    <div className="mt-1 space-y-0.5 border-t pt-1 font-mono text-[10px] leading-relaxed text-foreground/80">
      {/* Bar */}
      {hasBar && (
        <div className="flex h-5 w-full gap-px overflow-hidden rounded">
          {activeBar.map((seg) => {
            const ms = barVals[seg.key];
            const narrow = ms / totalBar < 0.18;
            return (
              <div
                key={seg.key}
                className={cn(seg.color, "flex min-w-0 items-center justify-center overflow-hidden text-white text-[10px]")}
                style={{ flexGrow: ms, flexBasis: 0 }}
                title={`${seg.label} ${Math.round(ms)}ms`}
              >
                <span className="truncate px-1">{narrow ? `${Math.round(ms)}` : `${seg.label} ${Math.round(ms)}`}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* TTFB chain */}
      {hasTtfb && (
        <div className="flex flex-wrap items-center gap-x-1">
          <span className="whitespace-nowrap font-medium text-muted-foreground/60">TTFB</span>
          {ttfb.map((s, i) => (
            <Fragment key={s.label}>
              {i > 0 && <span className="text-muted-foreground/30">→</span>}
              <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                <span className={cn("size-1.5 rounded-full shrink-0", s.dot)} />
                <span className="text-muted-foreground/60">{s.label}</span>
                <span className="tabular-nums">{s.cum}<span className="text-muted-foreground/40">ms</span></span>
                {s.delta !== null && <span className="tabular-nums text-muted-foreground/40">+{s.delta}</span>}
              </span>
            </Fragment>
          ))}
        </div>
      )}

      {/* Stats: stage-grouped, one line */}
      {hasStats && (
        <div className="flex flex-wrap items-center gap-x-1">
          {groups.map((group, i) => (
            <Fragment key={i}>
              {i > 0 && <span className="text-muted-foreground/20">│</span>}
              {group}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
